import { useState } from 'react';
import useScrollReveal from '@/hooks/useScrollReveal';
import { menuCategories } from '@/mocks/menu';
import SectionHeading from '@/components/feature/SectionHeading';

export default function MenuSection() {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number>(0);

  const toggle = (idx: number) => setOpenIndex((prev) => (prev === idx ? -1 : idx));

  return (
    <section id="ponudba" className="relative py-16 md:py-24 px-4 md:px-6 bg-background-100 scroll-mt-16 md:scroll-mt-20" ref={ref}>
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <SectionHeading
          eyebrow="Naša ponudba"
          title="Kaj ti danes paše?"
          description="Klikni na kategorijo in odkrij, kaj vse ti pripravimo — od jutranje kave do večernega sproščanja."
        />

        {/* Featured image */}
        <div
          className={`relative mb-10 md:mb-14 rounded-lg overflow-hidden border border-background-200/60 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <img
            src="https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/84c931a8e966565c42b5cec347d0f6fd.png"
            alt="Ledeno hladne pijače Salitos v vedru z ledom in svežim sadjem"
            title="Osvežilne pijače v Guma Caffe Lounge Bar Gorišnica"
            className="w-full h-[210px] md:h-[340px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
            <p className="text-xs font-semibold tracking-widest uppercase text-accent-300 mb-1">
              Osvežitev
            </p>
            <p className="text-foreground-50 text-sm md:text-lg font-heading font-bold leading-snug">
              Ledeno hladna pijača za pravo vzdušje
            </p>
          </div>
        </div>

        {/* Accordion Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          {menuCategories.map((cat, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`bg-background-50 border rounded-lg overflow-hidden transition-all duration-500 ${
                  isOpen ? 'border-accent-400/40' : 'border-background-200/60'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${Math.min(idx * 80, 400)}ms` }}
              >
                <button
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center gap-4 p-5 text-left cursor-pointer hover:bg-background-100/60 transition-colors duration-300"
                  aria-expanded={isOpen}
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-500/10 flex-shrink-0">
                    <i className={`${cat.icon} text-accent-400 text-lg`}></i>
                  </div>
                  <h3 className="flex-1 min-w-0 text-sm font-semibold tracking-wide text-foreground-100">
                    {cat.title}
                  </h3>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 text-lg transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isOpen ? 'max-h-[2600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 pb-5 pt-1 space-y-2.5">
                    {cat.items.map((item, iIdx) => (
                      <div key={iIdx} className="flex items-start gap-2.5">
                        <span className="mt-[7px] w-1 h-1 rounded-full bg-accent-400 flex-shrink-0"></span>
                        <span className="text-[13px] md:text-sm text-foreground-300 leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <p className="mt-10 md:mt-14 text-center text-xs md:text-sm text-foreground-500 italic max-w-xl mx-auto">
          Ponudba se lahko razlikuje glede na sezono. Za točno ponudbo povprašajte naše osebje na lokaciji.
        </p>
      </div>
    </section>
  );
}